import { useState } from 'react';

import styled from '@emotion/styled';
import { Button, Colors, Flex, Input, Text, typographyStyle } from '@revenuemarket/cds';
import { ResponseStatus } from '@revenuemarket/http';
import { Toast } from '@revenuemarket/ui';
import { formatMoney, mixpanelTrack } from '@revenuemarket/utils';
import { DatePicker, ModalProps } from 'antd';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash-es';

import CommonModal from '~components/ui/Modal/CommonModal';
import { useMappingTransactionToTaxInvoice } from '~hooks/queries/mapping/query';
import { useGetTaxInvoiceMappingsById } from '~hooks/queries/tax-invoice/query';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import { css } from '~libs/emotion';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { TaxInvoiceType } from '~models/tax-invoice';

import { formatNumByTaxInvoice } from './utils';

interface Props extends ModalProps {
  taxInvoiceId: string;
  taxInvoiceType: TaxInvoiceType;
  onMappingSuccess: () => void;
  onClose?: () => void;
}

const SettlementDirectMappingModal: React.FC<Props> = ({
  taxInvoiceId,
  taxInvoiceType,
  onMappingSuccess,
  onClose,
  ...props
}) => {
  const { companyId } = useDemoAwareCompanyId();
  const result = useGetTaxInvoiceMappingsById({
    companyId,
    taxInvoiceId,
    type: taxInvoiceType,
  });
  const { mutateAsync: mapTransactionsToTaxInvoice, isLoading } =
    useMappingTransactionToTaxInvoice();
  const mapping = result.data?.Data?.data;

  const [form, setForm] = useState<{
    reportingDate: string;
    memo: string;
  }>({
    reportingDate: mapping?.reportingDate ?? dayjs().format('YYYY-MM-DD'),
    memo: '',
  });

  const handleSubmit = async () => {
    const { reportingDate, memo } = form;

    const response = await mapTransactionsToTaxInvoice({
      companyId,
      taxInvoiceId,
      type: taxInvoiceType,
      data: {
        transactionLabels: [],
        invoiceAdjustments: [
          {
            adjustmentAmount: formatNumByTaxInvoice(mapping?.totalAmount ?? 0, taxInvoiceType),
            reportingDate,
            memo,
          },
        ],
      },
    });

    if (response.Status === ResponseStatus.SUCCESS) {
      Toast.success('정산 내역이 저장되었습니다.');
      mixpanelTrack(MIXPANEL_EVENT.SETTLEMENT.MAPPING, {
        type: taxInvoiceType,
      });
      mixpanelTrack(MIXPANEL_EVENT.ENGAGEMENT.DEFAULT, {
        type: taxInvoiceType === 'PURCHASE' ? 'settle purchases' : 'settle sales',
      });
      onMappingSuccess();
      onClose?.();
    } else {
      Toast.error('정산 내역 저장에 실패했습니다.');
    }
  };

  return (
    <CommonModal {...props} title="은행 거래내역 없이 즉시 정산" width={480} footer={null}>
      <Flex direction="column" gap={16}>
        <Flex direction="column" gap={24} style={{ marginTop: 24 }}>
          <CounterPartyInfo justify="space-between">
            <Text textStyle="14_600" style={{ color: Colors.gray800 }}>
              {mapping?.counterPartyCompanyName}
            </Text>
            <Text textStyle="14_600" style={{ color: Colors.gray800 }}>
              {formatMoney(mapping?.totalAmount, false, false, '원')}
            </Text>
          </CounterPartyInfo>
          <Flex gap={8}>
            <Flex direction="column" gap={8}>
              <Text textStyle="14_400" style={{ color: Colors.gray800 }}>
                날짜
              </Text>
              <DatePicker
                size="middle"
                clearIcon={null}
                disabledDate={(current) => {
                  // 오늘 이후는 선택 불가
                  return current && current > dayjs().endOf('day');
                }}
                suffixIcon={null}
                onChange={(value) => {
                  setForm({
                    ...form,
                    reportingDate: value?.format('YYYY-MM-DD') ?? '',
                  });
                }}
                format={'YY-MM-DD'}
                value={dayjs(form.reportingDate)}
                className={css`
                  cursor: pointer;
                  &&& .ant-picker-input > input {
                    width: 64px;
                    height: 24px;
                    color: ${Colors.gray700};
                    ${typographyStyle['14_500']};
                    cursor: pointer;
                  }
                `}
              />
            </Flex>
            <Flex direction="column" gap={8} style={{ flex: 1 }}>
              <Text textStyle="14_400" style={{ color: Colors.gray800 }}>
                메모
              </Text>
              <Input
                onChange={(e) => {
                  setForm({
                    ...form,
                    memo: e.target.value,
                  });
                }}
                value={form.memo}
              />
            </Flex>
          </Flex>
        </Flex>
        <Flex justify="flex-end" gap={8}>
          <Button buttonSize="large" onClick={onClose}>
            닫기
          </Button>
          <Button
            buttonSize="large"
            buttonType="primary"
            onClick={handleSubmit}
            disabled={isEmpty(form.reportingDate) || isEmpty(form.memo)}
            loading={isLoading}
          >
            정산하기
          </Button>
        </Flex>
      </Flex>
    </CommonModal>
  );
};

export default SettlementDirectMappingModal;

const CounterPartyInfo = styled(Flex)`
  padding: 8px;
  border-radius: 12px;
  background-color: ${Colors.gray50};
`;
